import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@services/api';
import Icon from '@material-ui/core/Icon';
import TextField from '@components/TextField';
import BreadCrumbs from '@components/Breadcrumbs';

// Feature "Consulta de custos operacionais" (2026-08-19): tela de LEITURA,
// visível pra todo mundo logado (ver admin.js), pra qualquer pessoa conferir
// o custo operacional de um passeio — grupo ou privado — antes de vender,
// sem precisar abrir o cadastro/edição do tour (que é restrito a
// administrador). Nasceu do bug de custo operacional de passeio privado
// (ver bug_custo_operacional_privado_multiplicado.md) — vários tours ainda
// não têm o custo de faixa privada cadastrado, então essa tela avisa em
// vermelho quando falta, pra ninguém vender sem saber o custo real.
const money = (v) => {
  const n = Number(v) || 0
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const OperationalCosts = (props) => {
  document.title = `Consulta de custos operacionais - ${process.env.REACT_APP_NAME}`

  const [q, setQ] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [selectedTour, setSelectedTour] = useState(null)
  const [details, setDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const wrapperRef = useRef()

  const bradecrumbsConfig = [
    { title: 'Início', active: true, route: '/admin' },
    { title: 'Consulta de custos operacionais' },
  ]

  // Fecha a lista de sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!q || q.length < 2) {
      setSuggestions([])
      return
    }
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      setLoadingSuggestions(true)
      apiClient.httpInstance.get('/tours', {
        params: { q, array: true },
        signal: controller.signal,
      }).then((response) => {
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || [])
          setSuggestions(data.slice(0, 20))
          setShowSuggestions(true)
        }
        setLoadingSuggestions(false)
      }).catch(() => {
        setLoadingSuggestions(false)
      })
    }, 350)
    return () => {
      controller.abort()
      clearTimeout(timeoutId)
    }
  }, [q])

  const selectTour = (tour) => {
    setSelectedTour(tour)
    setQ(tour.name)
    setShowSuggestions(false)
    setLoadingDetails(true)
    setDetails(null)
    apiClient.httpInstance.get(`/tours/${tour.id}/operational_costs`).then((response) => {
      if (response.status === 200) {
        setDetails(response.data)
      }
      setLoadingDetails(false)
    }).catch(() => {
      setLoadingDetails(false)
    })
  }

  const isGroupGap = (side) => side && side.value > 0 && side.operational_cost === 0 && side.operational_cost_sale === 0

  const isPrivateFullGap = (band) => band.value > 0 && band.cost === 0 && band.cost_sale === 0
  const isPrivatePartialGap = (band) => !isPrivateFullGap(band) && band.value > 0 && ((band.cost === 0) !== (band.cost_sale === 0))

  return <>
    <BreadCrumbs items={bradecrumbsConfig} />
    <div className="py-7 px-7 pt-6">
      <div className="flex items-center mt-2 text-2xl text-blue font-poppins mb-4">
        <Icon className="mr-2">manage_search</Icon>
        <span>Consulta de custos operacionais</span>
      </div>

      <div className="p-4 bg-white border-1" style={{ borderRadius: '10px' }}>
        <div className="relative" ref={wrapperRef} style={{ maxWidth: '520px' }}>
          {/* Fix (2026-08-20): TextField só encolhe o label (shrink) quando tem
              value — com label E placeholder juntos e campo vazio, os dois
              textos ficam sobrepostos dentro do campo. Mantém só o label,
              igual ao resto do painel (ex: ToursTable.jsx). */}
          <TextField
            className="inputRounded"
            label="Busque um passeio pelo nome"
            variant="outlined"
            type="text"
            // Fix (2026-08-20): "q" é reaproveitado em várias buscas do painel
            // (ToursTable.jsx etc.) e o navegador guarda histórico de autofill
            // por nome de campo — sobrepunha uma lista nativa (branca) por cima
            // da nossa. Nome único + autoComplete off evita isso.
            name="operational_costs_tour_search"
            autoComplete="off"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setSelectedTour(null)
              setDetails(null)
            }}
            onFocus={() => { if (suggestions.length) setShowSuggestions(true) }}
          />
          {showSuggestions && (
            <div
              className="absolute bg-white border-1 shadow-lg z-10 w-full overflow-auto"
              style={{ maxHeight: '320px', borderRadius: '8px', top: '100%', marginTop: '4px' }}
            >
              {/* Fix (2026-08-20): esse painel usa um tema escuro com paleta
                  Tailwind customizada — "gray" aqui NÃO tem variações numeradas
                  (gray-500, gray-100 etc. simplesmente não existem/não geram
                  CSS). O texto ficava com a cor padrão do navegador (preto),
                  ilegível no fundo escuro. Troca pra classe "text-gray"
                  (singular, mapeada pelo tema escuro) igual ao resto do painel. */}
              {loadingSuggestions && <div className="p-3 text-sm text-gray">Buscando...</div>}
              {!loadingSuggestions && suggestions.length === 0 && (
                <div className="p-3 text-sm text-gray">Nenhum passeio encontrado.</div>
              )}
              {!loadingSuggestions && suggestions.map((tour) => (
                <div
                  key={tour.id}
                  className="p-3 cursor-pointer text-sm border-b"
                  style={{ transition: 'background-color 0.1s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e3a5a' }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                  onClick={() => selectTour(tour)}
                >
                  <div className="font-medium text-blue">{tour.name}</div>
                  {tour.region_name && <div className="text-xs text-gray">{tour.region_name}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loadingDetails && (
        <div className="p-4 mt-4 bg-white border-1 text-center text-gray" style={{ borderRadius: '10px' }}>
          Carregando...
        </div>
      )}

      {/* Fix (2026-08-20): esse painel usa tema escuro — cards "bg-white" viram
          navy automaticamente (ver template.scss), mas o TEXTO dentro não muda
          de cor sozinho. Só ".text-blue" (texto normal) e ".text-gray"
          (texto secundário) são recoloridos pro escuro; qualquer outra classe
          (text-gray-500, sem classe nenhuma) fica preta e some no fundo
          escuro. Por isso a classe "text-blue" agora está no container de
          cada card — ela cascateia por herança de "color" pros filhos que não
          têm cor própria (Valor, C. op. etc.), e "text-gray" fica só nos
          rótulos secundários. Vermelho/amarelo de aviso continuam explícitos,
          essas classes já tinham paleta própria (não dependem do tema). */}
      {details && !loadingDetails && (
        <div className="mt-4 space-y-4">

          {/* Passeio em grupo */}
          <div className="bg-white border-1 text-blue" style={{ borderRadius: '10px' }}>
            <div className="px-4 py-3 border-b font-medium">Passeio em grupo</div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Adulto', side: details.group.adult },
                ...(details.group.kids_allowed ? [{ label: 'Criança', side: details.group.kid }] : []),
              ].map(({ label, side }) => {
                const gap = isGroupGap(side)
                return (
                  <div
                    key={label}
                    className="p-3 border-1"
                    style={{ borderRadius: '8px', backgroundColor: gap ? '#FDECEA' : 'transparent' }}
                  >
                    <div className={`font-medium mb-2 ${gap ? 'text-red-700' : ''}`}>{label}</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className={gap ? 'text-red-700' : 'text-gray'}>Valor:</span> {money(side.value)}</div>
                      <div><span className={gap ? 'text-red-700' : 'text-gray'}>C. op. Parceiro:</span> {money(side.operational_cost)}</div>
                      <div><span className={gap ? 'text-red-700' : 'text-gray'}>C. op. Venda:</span> {money(side.operational_cost_sale)}</div>
                    </div>
                    {gap && (
                      <div className="mt-2 text-xs text-red-700 font-medium">
                        CUSTO OPERACIONAL NÃO CADASTRADO — consultar antes de vender
                      </div>
                    )}
                  </div>
                )
              })}
              {!details.group.kids_allowed && (
                <div className="p-3 text-sm text-gray flex items-center">Passeio não permite crianças.</div>
              )}
            </div>
          </div>

          {/* Passeio privado */}
          <div className="bg-white border-1 text-blue" style={{ borderRadius: '10px' }}>
            <div className="px-4 py-3 border-b font-medium">Passeio privado — por quantidade de pessoas</div>
            {details.private.length === 0 ? (
              <div className="p-4 text-sm text-gray">Esse passeio não tem faixas de privado cadastradas.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray border-b">
                      <th className="p-3">Pessoas</th>
                      <th className="p-3">Valor</th>
                      <th className="p-3">C. op. Parceiro</th>
                      <th className="p-3">C. op. Venda</th>
                      <th className="p-3">Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.private.map((band) => {
                      const fullGap = isPrivateFullGap(band)
                      const partialGap = isPrivatePartialGap(band)
                      return (
                        <tr
                          key={band.id}
                          className="border-b"
                          style={{ backgroundColor: fullGap ? '#FDECEA' : partialGap ? '#FFF4E5' : 'transparent' }}
                        >
                          <td className={`p-3 ${fullGap ? 'text-red-700' : partialGap ? '' : 'text-blue'}`} style={partialGap ? { color: '#B26A00' } : undefined}>{band.min_people}-{band.max_people}</td>
                          <td className={`p-3 ${fullGap ? 'text-red-700' : partialGap ? '' : 'text-blue'}`} style={partialGap ? { color: '#B26A00' } : undefined}>{money(band.value)}</td>
                          <td className={`p-3 ${fullGap ? 'text-red-700 font-medium' : partialGap ? 'font-medium' : 'text-blue'}`} style={partialGap ? { color: '#B26A00' } : undefined}>{money(band.cost)}</td>
                          <td className={`p-3 ${fullGap ? 'text-red-700 font-medium' : partialGap ? 'font-medium' : 'text-blue'}`} style={partialGap ? { color: '#B26A00' } : undefined}>{money(band.cost_sale)}</td>
                          <td className="p-3">
                            {fullGap && (
                              <span className="text-red-700 text-xs font-medium">
                                CUSTO NÃO CADASTRADO — consultar antes de vender
                              </span>
                            )}
                            {partialGap && (
                              <span className="text-xs font-medium" style={{ color: '#B26A00' }}>
                                CUSTO PARCIAL — falta completar
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </>
}

export default OperationalCosts;
